import { Check, ChevronsUpDown, TriangleAlert } from "lucide-react";
import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import {
  AuthContext,
  AuthContextInterface,
} from "@/shared/lib/authorization/auth-context";
import { Role, RoleList } from "@/shared/lib/authorization/roles";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import { Label } from "@/shared/components/ui/label";
import {
  clearDevTokenOverrides,
  DevToken,
  devToken,
  persistDevTokenOverrides,
  resetDevTokenToEnv,
} from "@/shared/lib/authorization/dev-token";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { backendApi } from "@/services/backend/api";
import { useAppDispatch } from "@/shared/hooks/redux-hooks";
import * as Sentry from "@sentry/react";
import { CompaniesSelect } from "@/services/backend/company/components/companies-select";
import { BranchesSelect } from "@/services/backend/branch/components/branches-select";
import { FrontendUser } from "./frontendUser";

/**
 * DevAuthContextProvider sets a default user in the AuthContext and
 * allows the developer to modify the role of the user for easier development.
 *
 * @param props
 * @constructor
 */
export function DevAuthContextProvider(props: PropsWithChildren) {
  const { children } = props;
  const [user, setUser] = useState<FrontendUser>({
    id: devToken.id,
    fullname: "Dev User",
    roles: devToken.roles,
    initials: "DU",
    email: "",
  });

  // This is needed to pre-fill the user feedback
  // form of Sentry with the name of the user.
  useEffect(() => {
    Sentry.setUser({ fullName: user?.fullname, email: user?.email });
  }, [user?.email, user?.fullname]);

  const devAuthContext: AuthContextInterface = useMemo(
    () => ({
      user,
      login: (u: FrontendUser) => setUser(u),
      logout: () => null,
    }),
    [user],
  );

  return (
    <AuthContext.Provider value={devAuthContext}>
      <div>{children}</div>
      <div className="fixed right-6 bottom-6">
        <DevTokenDialog setRoles={(roles) => setUser({ ...user, roles })} />
      </div>
    </AuthContext.Provider>
  );
}

function DevTokenDialog({ setRoles }: { setRoles: (roles: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState<DevToken>(devToken);

  const dispatch = useAppDispatch();

  const doApply = () => {
    devToken.roles = token.roles;
    devToken.companies = token.companies;
    devToken.branches = token.branches;
    persistDevTokenOverrides({
      roles: token.roles,
      companies: token.companies,
      branches: token.branches,
    });

    setRoles(token.roles);

    // Triggers a reset of the backend api state
    // such that all query data is re-loaded with
    // the new token settings.
    // Note, that this does not reset the state
    // in local hooks, which should generally not
    // be a problem here though.
    dispatch(backendApi.util.resetApiState());

    setOpen(false);
  };

  const resetToEnv = () => {
    clearDevTokenOverrides();
    const defaults = resetDevTokenToEnv();
    setToken({ ...devToken, ...defaults });
    setRoles(defaults.roles);
    dispatch(backendApi.util.resetApiState());
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="shadow-xl">
          Dev User Config
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl min-w-fit">
        <DialogHeader>
          <DialogTitle>Dev Token Configuration</DialogTitle>
          <DialogDescription>
            Configure the roles, company association and branch association of
            the dev user.
          </DialogDescription>
        </DialogHeader>
        <Alert>
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>Attention</AlertTitle>
          <AlertDescription>
            Be aware that when you downgrade the roles and associations you may
            not be allowed to access all companies and branches anymore. In such
            a case, you can either reload the page or set the Admin role to get
            full access again.
          </AlertDescription>
        </Alert>
        <RoleSelect
          roles={token.roles}
          onRolesChange={(roles) => setToken({ ...token, roles })}
        />
        <CompaniesSelect
          companies={token.companies}
          setCompanies={(companies) => setToken({ ...token, companies })}
        />
        <BranchesSelect
          value={token.branches}
          onValueChange={(branches) => setToken({ ...token, branches })}
          companyFilter={{ active: true, values: token.companies }}
        />
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
          <Button variant="outline" onClick={resetToEnv}>
            Reset to ENV
          </Button>
          <Button onClick={doApply}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RoleSelect({
  roles,
  onRolesChange: setRoles,
  hideLabel,
}: {
  roles: Role[];
  onRolesChange: (roles: Role[]) => void;
  hideLabel?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="grid gap-1.5">
      {!hideLabel && <Label>Roles</Label>}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between"
          >
            {roles.length > 0 ? roles.join(", ") : "Select Roles..."}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            <CommandInput placeholder="Search Roles..." className="h-9" />
            <CommandEmpty>No role found.</CommandEmpty>
            <CommandGroup>
              {RoleList.map((role) => (
                <CommandItem
                  key={role}
                  value={role}
                  onSelect={() => {
                    if (roles.includes(role)) {
                      setRoles(roles.filter((r) => r !== role));
                    } else {
                      setRoles([...roles, role]);
                    }
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      roles.includes(role) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {role}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
